/*!
 * CHIEF - Cybersecurity Learning Platform (PROPRIETARY SOFTWARE)
 * Dashboard Component
 * 
 * Copyright (c) 2025 Srivatsan Sk. All rights reserved.
 * 
 * PROPRIETARY LICENSE NOTICE:
 * This software is the exclusive proprietary property of Srivatsan Sk.
 * Licensed to client under strict commercial license agreement.
 * Unauthorized use, copying, modification, distribution, or removal
 * of copyright notices is strictly prohibited.
 * 
 * Original Author: Srivatsan Sk
 * License: Proprietary/Commercial
 * Status: Confidential
 * 
 * WARNING: This code contains proprietary business logic and algorithms.
 * Reverse engineering, decompilation, or derivative works are prohibited.
 */

// src/pages/Dashboard.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Layout from '../Layout/Layout';
import Terminal from '../components/Terminal';
import { useAuth } from '../contexts/AuthContext';
import { usePageNotifications } from '../hooks/usePageNotifications';
import api from '../utils/apiClient';
import { handleApiError } from '../utils/errorHandler';
import {
    FiBarChart2,
    FiBookOpen,
    FiTarget,
    FiAward,
  FiChevronRight,
  FiTrendingUp,
    FiCalendar,
    FiPlay,
  FiArrowRight,
} from 'react-icons/fi';

// Avatar helpers styled similarly to Leaderboard
const MiniUserAvatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.background};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  background: ${({ background }) => background};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.06);

  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: ${({ glowColor }) => glowColor};
    border-radius: inherit;
    z-index: -1;
    opacity: 0.45;
    filter: blur(4px);
  }
`;

// Styled Components
const DashboardContainer = styled.div`
  min-height: calc(100vh - 70px);
`;

const WelcomeSection = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[6]};
  margin-bottom: ${({ theme }) => theme.spacing[8]};
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const WelcomeContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const WelcomeTitle = styled.h1`
  font-size: ${({ theme }) => theme.fonts.sizes['4xl']};
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  
  span {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.gradientPrimary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  
  @media (max-width: 1024px) {
    font-size: ${({ theme }) => theme.fonts.sizes['3xl']};
  }
`;

const WelcomeSubtitle = styled.p`
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  line-height: 1.6;
  
  @media (max-width: 1024px) {
    font-size: ${({ theme }) => theme.fonts.sizes.md};
  }
`;

const StatsGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.spacing[6]};
  margin-bottom: ${({ theme }) => theme.spacing[8]};
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${({ theme }) => theme.spacing[4]};
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[5]};
  box-shadow: ${({ theme }) => theme.shadows.subtle};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 4px;
    background: ${({ theme }) => theme.colors.gradientPrimary};
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
    border-color: ${({ theme }) => theme.colors.primary}30;
    transition: all ${({ theme }) => theme.transitions.normal};
  }
`;

const StatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const StatTitle = styled.h3`
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const StatIconWrapper = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.fonts.sizes['3xl']};
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const StatSubtext = styled.div`
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  display: flex;
  align-items: center;
  
  &::before {
    content: '';
    display: inline-block;
    width: 4px;
    height: 24px;
    background: ${({ theme }) => theme.colors.gradientPrimary};
    margin-right: ${({ theme }) => theme.spacing[3]};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }
`;

const EmptyState = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[6]};
  text-align: center;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  margin-bottom: ${({ theme }) => theme.spacing[8]};
`;

const EmptyStateTitle = styled.h3`
  font-size: ${({ theme }) => theme.fonts.sizes.xl};
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const EmptyStateDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[5]};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[5]};
`;

const ViewAllLink = styled(Link)`
  display: flex;
  align-items: center;
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  color: ${({ theme }) => theme.colors.primary};
  
  svg {
    margin-left: ${({ theme }) => theme.spacing[1]};
    transition: transform ${({ theme }) => theme.transitions.fast};
  }
  
  &:hover svg {
    transform: translateX(4px);
  }
`;

const EnrollButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.gradientPrimary};
  color: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[6]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  box-shadow: ${({ theme }) => theme.shadows.glow};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  svg {
    margin-left: ${({ theme }) => theme.spacing[2]};
    transition: transform ${({ theme }) => theme.transitions.fast};
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.strong};
    
    svg {
      transform: translateX(4px);
    }
  }
`;

const Grid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[6]};
  margin-bottom: ${({ theme }) => theme.spacing[8]};
`;

const CoursesGrid = styled(Grid)`
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
`;

const TwoColumnGrid = styled(Grid)`
  grid-template-columns: 2fr 1fr;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const CourseCard = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  transition: all ${({ theme }) => theme.transitions.normal};
  display: flex;
  flex-direction: column;
  height: 100%;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }) => theme.shadows.cardHover};
    border-color: ${({ theme }) => theme.colors.primary}30;
  }
`;

const CourseImageContainer = styled.div`
  height: 160px;
  position: relative;
  overflow: hidden;
  background: ${({ gradient }) => gradient || 'linear-gradient(135deg, #0AFF70 0%, #09B6FF 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CourseLetter = styled.div`
  font-size: 64px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.8);
`;

const DifficultyBadge = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing[3]};
  left: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ difficulty, theme }) => {
        switch (difficulty) {
            case 'beginner': return theme.colors.beginner;
            case 'intermediate': return theme.colors.intermediate;
            case 'advanced': return theme.colors.advanced;
            case 'expert': return theme.colors.expert;
            default: return theme.colors.beginner;
        }
    }};
  color: ${({ theme }) => theme.colors.background};
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  font-weight: 600;
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  text-transform: capitalize;
`;

const CourseContent = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const CourseTitle = styled.h3`
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  font-weight: 600;
  margin: 0 0 ${({ theme }) => theme.spacing[3]} 0;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${({ value }) => value}%;
    background: ${({ theme }) => theme.colors.gradientPrimary};
    border-radius: ${({ theme }) => theme.borderRadius.full};
    transition: width 0.5s ease;
  }
`;

const ProgressText = styled.div`
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  display: flex;
  justify-content: space-between;
`;

const CourseMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: ${({ theme }) => theme.spacing[3]};
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
`;

const CourseMetaItem = styled.div`
  display: flex;
  align-items: center;
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[1]};
  }
`;

const ContinueButton = styled(Link)`
  display: flex;
  align-items: center;
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 500;
  
  svg {
    margin-left: ${({ theme }) => theme.spacing[1]};
    transition: transform ${({ theme }) => theme.transitions.fast};
  }
  
  &:hover svg {
    transform: translateX(4px);
  }
`;

const Card = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const CardHeader = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CardTitle = styled.h3`
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const CardContent = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
`;

const AchievementsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const AchievementItem = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border: 1px solid ${({ theme }) => theme.colors.divider};
`;

const AchievementIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.gradientPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fonts.sizes.xl};
  color: ${({ theme }) => theme.colors.background};
  margin-right: ${({ theme }) => theme.spacing[3]};
  flex-shrink: 0;
`;

const AchievementInfo = styled.div`
  flex: 1;
`;

const AchievementTitle = styled.div`
  font-weight: 500;
  margin-bottom: 2px;
`;

const AchievementDescription = styled.div`
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const AchievementPoints = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  
  span {
    margin-right: ${({ theme }) => theme.spacing[1]};
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
`;

const Dashboard = () => {
    const { currentUser } = useAuth();
    // Get notification functions for manual control
    const { refreshNotificationsIfNeeded } = usePageNotifications(false); // Don't auto-refresh
    const [dashboardData, setDashboardData] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);  const terminalCommands = [
    { text: 'Initializing secure session...', delay: 500 },
    { text: `Welcome back, ${currentUser?.name || 'User'}!`, delay: 800 },
    { text: 'Your cyber security journey awaits', delay: 1000 },
    // { text: 'Let\'s learn together', delay: 1000 }
  ];

    // Fetch dashboard data and refresh notifications only on dashboard visit
    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setLoading(true);
              const data = await api.users.getDashboardStats();
                setDashboardData(data);

              // Fetch top leaderboard entries for dashboard card (best-effort)
              try {
                const lb = await api.users.leaderBoard();
                setLeaderboardData(Array.isArray(lb) ? lb.slice(0, 5) : []);
              } catch (lbErr) {
                console.warn('Failed to load leaderboard for dashboard card', lbErr);
                setLeaderboardData([]);
              }

                setError(null);

                // Only refresh notifications when visiting dashboard (not on every render)
                refreshNotificationsIfNeeded();
            } catch (err) {
                console.error('Error fetching dashboard data:', err);
                setError('Failed to load dashboard data. Please try again later.');

              // Use centralized error handling
              handleApiError(err, 'Failed to load dashboard data. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []); // Empty dependency to run only once when component mounts

    // Format date string
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

  const getInitials = (name) => {
    if (!name) return '';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const getAvatarStyle = (userId, rank) => {
    if (rank === 1) {
      return {
        background: 'linear-gradient(135deg, #0AFF70, #06C058)',
        glow: 'rgba(10, 255, 112, 0.6)'
      };
    }
    if (rank === 2) {
      return {
        background: 'linear-gradient(135deg, #09B6FF, #0788C0)',
        glow: 'rgba(9, 182, 255, 0.6)'
      };
    }
    if (rank === 3) {
      return {
        background: 'linear-gradient(135deg, #4C83FF, #3A63C0)',
        glow: 'rgba(76, 131, 255, 0.6)'
      };
    }

    const hue = (userId * 137) % 360;
    return {
      background: `linear-gradient(135deg, hsl(${hue}, 80%, 50%), hsl(${hue + 30}, 70%, 40%))`,
      glow: `hsla(${hue}, 80%, 50%, 0.4)`
    };
  };

    if (loading) {
        return (
            <Layout>
                <LoadingState>Loading dashboard data...</LoadingState>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <EmptyState>
                    <EmptyStateTitle>Something went wrong</EmptyStateTitle>
                    <EmptyStateDescription>{error}</EmptyStateDescription>
                    <EnrollButton as="button" onClick={() => window.location.reload()}>
                        Retry <FiArrowRight />
                    </EnrollButton>
                </EmptyState>
            </Layout>
        );
    }

    return (
        <Layout>
            <DashboardContainer>
                <WelcomeSection>
                    <WelcomeContent>
                        <WelcomeTitle>
                            Welcome back, <span>{currentUser?.name || 'User'}!</span>
                        </WelcomeTitle>
                        <WelcomeSubtitle>
                            Continue your cyber security journey and enhance your skills
                        </WelcomeSubtitle>
                    </WelcomeContent>

            {/* <Terminal commands={terminalCommands} /> 
                    */}
            <Terminal
              commands={terminalCommands}
              interactive={true}
              onCommand={(command) => {
                // Process user commands here
                // Terminal command processed
              }}
            />
                </WelcomeSection>

                <StatsGrid>
                    <StatCard
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.1 }}
                    >
                        <StatHeader>
                            <StatTitle>XP Points</StatTitle>
                            <StatIconWrapper>
                                <FiBarChart2 />
                            </StatIconWrapper>
                        </StatHeader>
                        <StatValue>{dashboardData?.xp_points || 0}</StatValue>
                        <StatSubtext>+0 this week</StatSubtext>
                    </StatCard>

                    <StatCard
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.2 }}
                    >
                        <StatHeader>
                            <StatTitle>Courses</StatTitle>
                            <StatIconWrapper>
                                <FiBookOpen />
                            </StatIconWrapper>
                        </StatHeader>
                        <StatValue>{dashboardData?.total_courses || 0}</StatValue>
                        <StatSubtext>
                            {dashboardData?.courses_completed || 0} completed
                        </StatSubtext>
                    </StatCard>

                    <StatCard
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.3 }}
                    >
                        <StatHeader>
                            <StatTitle>Completion</StatTitle>
                            <StatIconWrapper>
                                <FiTarget />
                            </StatIconWrapper>
                        </StatHeader>
              <StatValue>{Math.round(dashboardData?.completion_percentage || 0)}%</StatValue>
                        <StatSubtext>Overall progress</StatSubtext>
                    </StatCard>

                    <StatCard
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.4 }}
                    >
                        <StatHeader>
                            <StatTitle>Rank</StatTitle>
                            <StatIconWrapper>
                                <FiAward />
                            </StatIconWrapper>
                        </StatHeader>
                        <StatValue>
                            {dashboardData?.user_rank ? `#${dashboardData.user_rank}` : 'N/A'}
                        </StatValue>

                    </StatCard>
                </StatsGrid>

                {(!dashboardData?.active_courses || dashboardData.active_courses.length === 0) &&
                    (!dashboardData?.completion_by_course || dashboardData.completion_by_course.length === 0) && (
                        <EmptyState>
                            <EmptyStateTitle>You haven't enrolled in any courses yet!</EmptyStateTitle>
                            <EmptyStateDescription>
                                Explore our catalog of cyber security courses and start your learning journey today.
                            </EmptyStateDescription>
                            <EnrollButton to="/courses">
                                Browse Courses <FiArrowRight />
                            </EnrollButton>
                        </EmptyState>
                    )}

                {dashboardData?.completion_by_course && dashboardData.completion_by_course.length > 0 && (
                    <section>
                        <SectionHeader>
                            <SectionTitle>Your Courses</SectionTitle>
                            <ViewAllLink to="/learning">
                                View All <FiChevronRight />
                            </ViewAllLink>
                        </SectionHeader>

                        <CoursesGrid>
                            {dashboardData.completion_by_course.map((course) => (
                                <CourseCard
                                    key={course.course_id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <CourseImageContainer>
                                        {course.thumbnail ? (
                                            <img src={course.thumbnail} alt={course.course_title} />
                                        ) : (
                                            <CourseLetter>{course.course_title.charAt(0)}</CourseLetter>
                                        )}
                                        <DifficultyBadge difficulty={course.difficulty_level}>
                                            {course.difficulty_level}
                                        </DifficultyBadge>
                                    </CourseImageContainer>

                                    <CourseContent>
                                        <CourseTitle>{course.course_title}</CourseTitle>

                                        <ProgressBar value={course.overall_progress} />
                                        <ProgressText>
                                            <span>Progress</span>
                                            <span>{course.overall_progress.toFixed(1)}%</span>
                                        </ProgressText>

                                        <CourseMeta>
                                            <CourseMetaItem>
                                                <FiCalendar />
                                                <span>Enrolled: {formatDate(course.enrolled_at)}</span>
                                            </CourseMetaItem>

                                            <ContinueButton to={`/courses/${course.course_id}`}>
                                                Continue <FiPlay />
                                            </ContinueButton>
                                        </CourseMeta>
                                    </CourseContent>
                                </CourseCard>
                            ))}
                        </CoursesGrid>
                    </section>
                )}

                <TwoColumnGrid>
                    <Card>
                        <CardHeader>
                            <CardTitle><FiAward /> Recent Achievements</CardTitle>
                            <ViewAllLink to="/achievements">
                                View All <FiChevronRight />
                            </ViewAllLink>
                        </CardHeader>

                        <CardContent>
                            {dashboardData?.recent_achievements?.points &&
                                dashboardData.recent_achievements.points.length > 0 ? (
                                <AchievementsList>
                                    {dashboardData.recent_achievements.points.map((point, index) => (
                                        <AchievementItem key={index}>
                                            <AchievementIcon>
                                                <FiTarget />
                                            </AchievementIcon>
                                            <AchievementInfo>
                                                <AchievementTitle>{point.description}</AchievementTitle>
                                                <AchievementDescription>
                                                    Earned on {formatDate(point.earned_at)}
                                                </AchievementDescription>
                                            </AchievementInfo>
                                            <AchievementPoints>
                                                <span>{point.points}</span> XP
                                            </AchievementPoints>
                                        </AchievementItem>
                                    ))}
                                </AchievementsList>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                                    <p>No achievements yet. Keep learning to earn rewards!</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle><FiTrendingUp /> Leaderboard</CardTitle>
                            <ViewAllLink to="/leaderboard">
                                View All <FiChevronRight />
                            </ViewAllLink>
                        </CardHeader>

                        <CardContent>
                {leaderboardData && leaderboardData.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {leaderboardData.map((user, idx) => (
                      <div
                        key={user.user_id || user.id || idx}
                        onClick={() => window.location.href = `/leaderboard/${user.user_id || user.id}`}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          padding: '8px',
                          borderRadius: '8px',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(10, 255, 112, 0.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ width: 36, height: 36, borderRadius: 8, background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700 }}>
                              {idx + 1}
                            </div>
                            {/* Avatar like main leaderboard */}
                            {(() => {
                              const avatarStyle = getAvatarStyle(user.user_id || user.id || idx, user.rank || idx + 1);
                              return user.profile_image ? (
                                <MiniUserAvatar
                                  as="img"
                                  src={user.profile_image}
                                  alt={user.name}
                                  background={avatarStyle.background}
                                  glowColor={avatarStyle.glow}
                                />
                              ) : (
                                <MiniUserAvatar background={avatarStyle.background} glowColor={avatarStyle.glow}>
                                  {getInitials(user.name || user.username || user.display_name)}
                                </MiniUserAvatar>
                              );
                            })()}
                            <div>
                              <div style={{ fontWeight: 600 }}>{user.name || user.username || user.display_name || 'User'}</div>
                              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{user.email || ''}</div>
                            </div>
                          </div>
                        </div>
                        <div style={{ fontWeight: 700 }}>{user.reward_points ?? user.points ?? user.xp ?? 0} XP</div>
                      </div>
                    ))}
                    <div style={{ marginTop: 8, paddingTop: 12, borderTop: '1px solid var(--divider)', textAlign: 'center', color: 'var(--text-secondary)' }}>
                      <div style={{ marginBottom: 8 }}>You're currently ranked #{dashboardData?.user_rank || 'N/A'}</div>
                      <ViewAllLink to="/leaderboard" style={{ fontSize: '14px' }}>
                        View Full Leaderboard <FiChevronRight />
                      </ViewAllLink>
                    </div>
                  </div>
                ) : (
                            <div style={{ textAlign: 'center', padding: '20px 0' }}>
                      <p>You're currently ranked #{dashboardData?.user_rank || 'N/A'}</p>
                      <p>Keep learning to improve your position!</p>
                            </div>
                )}
                        </CardContent>
                    </Card>
                </TwoColumnGrid>
            </DashboardContainer>
        </Layout>
    );
};

export default Dashboard;